import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AuthUser,
  fetchUserAttributes,
  getCurrentUser,
  signIn,
  signOut,
  signUp,
  AuthError,
} from 'aws-amplify/auth';
import * as amplitude from '@amplitude/analytics-browser';
import { useLocation } from 'react-router';
import { CustomerRecord } from '../models/customer.models';
import { requestResetPasswordCode, updatePassword } from '../services/api';

export const useFetchUserAttributes = () => {
  return useQuery({
    queryKey: ['fetchUserAttributes'],
    queryFn: () => fetchUserAttributes(),
  });
};

export const useGetCurrentUser = () => {
  return useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      try {
        const user = await getCurrentUser();
        amplitude.setUserId(user.userId);
        return user;
      } catch (error) {
        // If the user is unauthenticated, return null, rather than throwing an error.
        if (
          error instanceof AuthError &&
          error.name === 'UserUnAuthenticatedException'
        ) {
          return null;
        }

        throw error;
      }
    },
  });
};

type LocationState = {
  customer?: CustomerRecord;
};

export const useGetSignedUserOrUserFromState = ():
  | CustomerRecord
  | AuthUser => {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const { data: currentUser } = useGetCurrentUser();

  const user: CustomerRecord | AuthUser = state?.customer
    ? { ...state.customer, userId: state.customer.pk }
    : (currentUser as AuthUser);

  return user;
};

export const useCreateAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['user/create-account'],
    mutationFn: signUp,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

export const useSignIn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['user/sign-in'],
    mutationFn: signIn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.invalidateQueries({ queryKey: ['favorites'] });
      await queryClient.invalidateQueries({
        queryKey: ['fetchUserAttributes'],
      });
    },
  });
};

export const useSignOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['user/sign-out'],
    mutationFn: () => {
      return signOut();
    },
    onSuccess: async () => {
      await queryClient.setQueryData(['user'], {});
      queryClient.clear();
    },
  });
};

export const useRequestResetPasswordCode = () => {
  return useMutation({
    mutationKey: ['forgot-password'],
    mutationFn: requestResetPasswordCode,
  });
};

export const useUpdatePassword = () => {
  return useMutation({
    mutationKey: ['forgot-password/confirm'],
    mutationFn: updatePassword,
  });
};
